import { Controller } from "@hotwired/stimulus";

// Dropdown generico — replica el comportamiento de Menu.tsx (MUI Menu).
// Al abrir, mueve el panel al <body> para escapar de cualquier stacking context
// (transform, overflow, z-index de containers padres). Posiciona con position:fixed
// calculando espacio disponible arriba/abajo y max-height con scroll.
//
// Uso basico (solo toggle):
//   <div data-controller="manage--dropdown">
//     <button data-action="manage--dropdown#toggle">Opciones</button>
//     <div data-manage--dropdown-target="menu" class="hidden">
//       ...items...
//     </div>
//   </div>
//
// Uso con seleccion + form submit:
//   <div data-controller="manage--dropdown">
//     <button data-action="manage--dropdown#toggle"
//             data-manage--dropdown-target="trigger">Estado</button>
//     <div data-manage--dropdown-target="menu" class="hidden">
//       <button data-action="manage--dropdown#select"
//               data-value="published">Publicado</button>
//     </div>
//     <form data-manage--dropdown-target="form">
//       <input data-manage--dropdown-target="input" type="hidden" name="state">
//     </form>
//   </div>
//
// Uso con dispatch (dropdown → evento custom en document):
//   Los items del dropdown se mueven a <body> y pierden la conexion Stimulus.
//   Los items con data-event disparan un CustomEvent en document al hacer click.
//   Un controller hermano (ej: outline-actions) escucha el evento.
//
//   <button data-event="outline-action" data-target-id="x" data-dispatch-action="openModal" data-modal="drip">
//     Goteo
//   </button>
//
// Alineacion horizontal (data-manage--dropdown-align-value):
//   "right" (default): borde derecho del panel = borde derecho del trigger
//   "left": borde izquierdo del panel = borde izquierdo del trigger

const VIEWPORT_MARGIN = 8;
const GAP = 4;
const openDropdowns = new Set();

export default class extends Controller {
  static targets = ["menu", "form", "input", "trigger"];
  static values = {
    align: { type: String, default: "right" },
    autoOpen: { type: Boolean, default: false },
  };

  toggle(event) {
    event.stopPropagation();
    event.preventDefault();

    if (this._isOpen) {
      this.close();
    } else {
      this.open(event.currentTarget);
    }
  }

  open(triggerEl) {
    openDropdowns.forEach((dd) => { if (dd !== this) dd.close(); });

    const menu = this._menuNode;
    const trigger = triggerEl || this.element.querySelector("button");

    if (!this._originalParent) {
      this._originalParent = menu.parentNode;
      this._originalNextSibling = menu.nextSibling;
    }

    document.body.appendChild(menu);

    menu.classList.remove("hidden");
    Object.assign(menu.style, {
      position: "fixed",
      visibility: "hidden",
      zIndex: "9999",
      top: "0",
      left: "0",
      maxHeight: "none",
      overflowY: "hidden",
    });

    const triggerRect = trigger.getBoundingClientRect();
    const menuWidth = menu.offsetWidth;
    const menuHeight = menu.offsetHeight;
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    const spaceBelow = vh - triggerRect.bottom - VIEWPORT_MARGIN - GAP;
    const spaceAbove = triggerRect.top - VIEWPORT_MARGIN - GAP;

    let top;
    let maxHeight;

    if (menuHeight <= spaceBelow) {
      top = triggerRect.bottom + GAP;
      maxHeight = spaceBelow;
    } else if (menuHeight <= spaceAbove) {
      top = triggerRect.top - GAP - menuHeight;
      maxHeight = spaceAbove;
    } else if (spaceBelow >= spaceAbove) {
      top = triggerRect.bottom + GAP;
      maxHeight = spaceBelow;
    } else {
      top = VIEWPORT_MARGIN;
      maxHeight = spaceAbove;
    }

    let left;
    if (this.alignValue === "left") {
      left = triggerRect.left;
    } else {
      left = triggerRect.right - menuWidth;
    }
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - VIEWPORT_MARGIN - menuWidth));

    Object.assign(menu.style, {
      top: `${Math.round(top)}px`,
      left: `${Math.round(left)}px`,
      maxHeight: `${Math.round(maxHeight)}px`,
      overflowY: "auto",
      visibility: "visible",
    });

    this._isOpen = true;
    openDropdowns.add(this);
  }

  close() {
    if (!this._isOpen) return;

    const menu = this._menuNode;
    menu.classList.add("hidden");
    menu.removeAttribute("style");
    this._isOpen = false;
    openDropdowns.delete(this);

    this._restoreMenu();
  }

  _restoreMenu() {
    const menu = this._menuNode;
    if (!menu || !this._originalParent) return;
    if (menu.parentNode === this._originalParent) return;

    if (this._originalNextSibling && this._originalNextSibling.parentNode === this._originalParent) {
      this._originalParent.insertBefore(menu, this._originalNextSibling);
    } else {
      this._originalParent.appendChild(menu);
    }
  }

  select(event) {
    event.stopPropagation();
    event.preventDefault();

    const value = event.currentTarget.dataset.value;

    if (this.hasInputTarget) {
      this.inputTarget.value = value;
    }

    this.close();

    if (this.hasTriggerTarget) {
      this.triggerTarget.disabled = true;
    }

    if (this.hasFormTarget) {
      this.formTarget.requestSubmit();
    }
  }

  clickOutside(event) {
    if (!this._isOpen) return;
    if (!this.element.contains(event.target) && !this._menuNode.contains(event.target)) {
      this.close();
    }
  }

  connect() {
    this._isOpen = false;
    this._menuNode = this.menuTarget;
    this._originalParent = null;
    this._originalNextSibling = null;
    this._clickOutside = this.clickOutside.bind(this);
    this._escHandler = (e) => {
      if (e.key === "Escape") this.close();
    };

    // Delegated click handler para items dentro del menu panel.
    // Necesario porque el panel se mueve a <body> y los items pierden
    // la conexion Stimulus con el controller original. Este handler:
    // 1. Cierra el dropdown
    // 2. Si el item tiene data-event, dispara un CustomEvent en document
    this._menuClickHandler = (e) => {
      const item = e.target.closest("[data-event]") || e.target.closest("[data-value]") || e.target.closest("button, a");
      if (!item || !this._menuNode.contains(item)) return;

      // Si el item tiene data-event, hacer close + dispatch
      if (item.dataset.event) {
        e.stopPropagation();
        e.preventDefault();
        this.close();
        const detail = Object.assign({}, item.dataset);
        document.dispatchEvent(new CustomEvent(item.dataset.event, { detail }));
        return;
      }

      // Si el item tiene data-value, replicar la logica de select()
      // Necesario porque al mover el menu a <body>, los data-action de Stimulus
      // pierden la conexion con el controller
      if (item.dataset.value) {
        e.stopPropagation();
        e.preventDefault();

        if (this.hasInputTarget) {
          this.inputTarget.value = item.dataset.value;
        }

        this.close();

        if (this.hasTriggerTarget) {
          this.triggerTarget.disabled = true;
        }

        if (this.hasFormTarget) {
          this.formTarget.requestSubmit();
        }
        return;
      }

      // Item normal (link o boton sin data-event/data-value): el panel vive en
      // <body>, asi que su data-action="manage--dropdown#close" no llega al
      // controller y el dropdown quedaba abierto flotando sobre el modal que
      // acababa de abrir. Se cierra en el proximo tick para no mover el nodo
      // mientras Turbo todavia esta procesando el click (turbo_frame, method).
      setTimeout(() => this.close(), 0);
    };
    this._menuNode.addEventListener("click", this._menuClickHandler);

    this._beforeCacheHandler = () => {
      if (this._isOpen) this.close();
      else this._restoreMenu();
    };
    document.addEventListener("turbo:before-cache", this._beforeCacheHandler);

    document.addEventListener("click", this._clickOutside);
    document.addEventListener("keydown", this._escHandler);

    if (this.autoOpenValue) {
      requestAnimationFrame(() => this.open());
    }
  }

  disconnect() {
    document.removeEventListener("click", this._clickOutside);
    document.removeEventListener("keydown", this._escHandler);
    document.removeEventListener("turbo:before-cache", this._beforeCacheHandler);
    if (this._menuNode) {
      this._menuNode.removeEventListener("click", this._menuClickHandler);
    }
    this.close();
  }
}
